import React, { useEffect, useState } from 'react';
import { Loan } from '../types';
import { Card } from './ui/Card';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import { Clock, User, Phone, Lock, Unlock, Timer } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
interface RetentionQueueProps {
  loans: Loan[];
  onActionClick: (loan: Loan) => void;
  onClaim: (loanId: string) => void;
  onRelease: (loanId: string) => void;
  currentUser: string;
}
export function RetentionQueue({
  loans,
  onActionClick,
  onClaim,
  onRelease,
  currentUser
}: RetentionQueueProps) {
  // Force re-render every second for the countdown timer
  const [, setTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(timer);
  }, []);
  const getDaysInRetention = (dateEntered: string) => {
    const start = new Date(dateEntered);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - start.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };
  const getUrgencyColor = (days: number) => {
    if (days > 5) return 'text-rose-600 bg-rose-50 border-rose-100';
    if (days > 2) return 'text-accent-600 bg-accent-50 border-accent-100';
    return 'text-emerald-600 bg-emerald-50 border-emerald-100';
  };
  const getRemainingTime = (claimedAt: string) => {
    const claimTime = new Date(claimedAt).getTime();
    const now = Date.now();
    const oneHour = 60 * 60 * 1000;
    const remainingMs = Math.max(0, claimTime + oneHour - now);
    const minutes = Math.floor(remainingMs / (1000 * 60));
    const seconds = Math.floor(remainingMs % (1000 * 60) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };
  return <Card noPadding className="overflow-hidden border-slate-200 bg-white" data-id="element-54">
      <div className="overflow-x-auto" data-id="element-55">
        <table className="w-full text-left text-base" data-id="element-56">
          <thead className="bg-brand-600 border-b border-brand-800" data-id="element-57">
            <tr data-id="element-58">
              <th className="px-5 py-3.5 font-semibold text-white text-base" data-id="element-59">
                Loan Details
              </th>
              <th className="px-5 py-3.5 font-semibold text-white text-base" data-id="element-60">
                Prior Stage
              </th>
              <th className="px-5 py-3.5 font-semibold text-white text-base" data-id="element-61">
                Team Lead
              </th>
              <th className="px-5 py-3.5 font-semibold text-white text-base" data-id="element-62">
                Timeline
              </th>
              <th className="px-5 py-3.5 font-semibold text-white text-base text-right" data-id="element-63">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-64">
            {loans.length === 0 ? <tr data-id="element-65">
                <td colSpan={5} className="px-4 py-8 text-center text-slate-500 text-base" data-id="element-66">
                  No active loans in retention queue. Good job!
                </td>
              </tr> : loans.map((loan, index) => {
            const days = getDaysInRetention(loan.dateEntered);
            return <motion.tr key={loan.id} initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: index * 0.03
            }} className="hover:bg-slate-50 transition-colors group" data-id="element-67">
                    <td className="px-5 py-4" data-id="element-68">
                      <Link to={`/loan/${loan.id}`} className="font-medium text-slate-900 text-lg leading-tight hover:text-brand-600 hover:underline transition-colors" data-id="element-69">
                        {loan.borrowerName}
                      </Link>
                      <div className="text-sm text-slate-500 font-mono mt-0.5" data-id="element-70">
                        {loan.loanNumber}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-sm text-slate-500" data-id="element-71">
                        {loan.claimedBy === currentUser && <span className="inline-flex items-center gap-1" data-id="element-72">
                            <Phone className="h-3.5 w-3.5" data-id="element-73" />
                            {loan.borrowerPhone}
                          </span>}
                        <span className="inline-flex items-center gap-1" data-id="element-74">
                          <User className="h-3.5 w-3.5" data-id="element-75" /> {loan.loanOfficer}
                        </span>
                      </div>
                    </td>
                    <td className="px-5 py-4" data-id="element-76">
                      <div className="max-w-xs" data-id="element-77">
                        <Badge variant="info" className="mb-1 text-sm px-2.5 py-0.5" data-id="element-78">
                          {loan.priorStage}
                        </Badge>
                        <p className="text-slate-600 text-base" data-id="element-79">
                          {loan.priorSubstatus}
                        </p>
                      </div>
                    </td>
                    <td className="px-5 py-4" data-id="element-80">
                      <div className="flex items-center gap-2" data-id="element-81">
                        <div className="h-7 w-7 rounded-full bg-brand-100 flex items-center justify-center text-xs font-bold text-brand-700" data-id="element-82">
                          {loan.pushedBy.split(' ').map(n => n[0]).join('').substring(0, 2)}
                        </div>
                        <span className="text-slate-700 text-base" data-id="element-83">
                          {loan.pushedBy}
                        </span>
                      </div>
                    </td>
                    <td className="px-5 py-4" data-id="element-84">
                      <div className={`inline-flex items-center px-2.5 py-1 rounded border text-sm font-medium ${getUrgencyColor(days)}`} data-id="element-85">
                        <Clock className="h-3.5 w-3.5 mr-1" data-id="element-86" />
                        {days}d
                      </div>
                      <div className="text-sm text-slate-500 mt-0.5" data-id="element-87">
                        {new Date(loan.dateEntered).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-5 py-4 text-right" data-id="element-88">
                      {!loan.claimedBy ? <Button size="sm" variant="outline" onClick={() => onClaim(loan.id)} className="opacity-0 group-hover:opacity-100 transition-opacity h-9 text-sm px-3 border-slate-300 text-slate-700 hover:bg-slate-100" data-id="element-89">
                          <Lock className="h-3.5 w-3.5 mr-1.5" data-id="element-90" />
                          Claim File
                        </Button> : loan.claimedBy === currentUser ? <div className="flex items-center justify-end gap-2" data-id="element-91">
                          <div className="flex items-center text-sm font-mono text-accent-700 bg-accent-50 px-2.5 py-1 rounded border border-accent-200" data-id="element-92">
                            <Timer className="h-4 w-4 mr-1" data-id="element-93" />
                            {loan.claimedAt && getRemainingTime(loan.claimedAt)}
                          </div>
                          <Button size="sm" variant="primary" onClick={() => onActionClick(loan)} className="h-9 text-sm px-3" data-id="element-94">
                            Take Action
                          </Button>
                        </div> : <div className="flex items-center justify-end gap-2" data-id="element-95">
                          <Badge variant="secondary" className="font-normal text-sm bg-slate-100 text-slate-600" data-id="element-96">
                            Claimed by {loan.claimedBy.split(' ')[0]}
                          </Badge>
                          <div className="flex items-center text-sm font-mono text-slate-400" data-id="element-97">
                            <Timer className="h-3.5 w-3.5 mr-1" data-id="element-98" />
                            {loan.claimedAt && getRemainingTime(loan.claimedAt)}
                          </div>
                        </div>}
                    </td>
                  </motion.tr>;
          })}
          </tbody>
        </table>
      </div>
    </Card>;
}